import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  TextField,
  Button,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Alert,
  Paper,
  CircularProgress,
  Divider,
  Chip,
  Avatar,
} from '@mui/material';
import {
  Send as SendIcon,
  ArrowBack as ArrowBackIcon,
  Public as PublicIcon,
  Group as GroupIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import api from '../utils/authUtils';

function CreatePrayer() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedCircleId = searchParams.get('circle');

  // Form state
  const [content, setContent] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [category, setCategory] = useState('General');
  const [circleId, setCircleId] = useState(preselectedCircleId || '');

  // Data state
  const [circles, setCircles] = useState([]);
  const [loadingCircles, setLoadingCircles] = useState(true);

  // UI state
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Fetch user's circles on mount
  useEffect(() => {
    const fetchCircles = async () => {
      try {
        const response = await api.get('/circles/my');
        // Only show approved memberships
        const approvedCircles = (response.data.circles || []).filter(
          c => c.membership_status === 'approved'
        );
        setCircles(approvedCircles);
      } catch (err) {
        console.error('Error fetching circles:', err);
      } finally {
        setLoadingCircles(false);
      }
    };

    fetchCircles();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!content.trim()) {
      setError('Prayer content is required.');
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        content: content.trim(),
        is_anonymous: isAnonymous,
        category,
      };

      // Add circle_id only if a circle is selected
      if (circleId) {
        payload.circle_id = parseInt(circleId);
      }

      await api.post('/prayers', payload);
      setSuccess(true);
      setContent('');
      setIsAnonymous(false);
      setCategory('General');

      // If posted to a circle, redirect there after a short delay
      if (circleId) {
        setTimeout(() => {
          navigate(`/circles/${circleId}`);
        }, 1500);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create prayer.');
    } finally {
      setSubmitting(false);
    }
  };

  const getSelectedCircle = () => {
    return circles.find(c => c.id === parseInt(circleId));
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
      <Paper
        elevation={3}
        sx={{
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h5" fontWeight="bold" sx={{ flexGrow: 1 }}>
            Write a Prayer
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Prayer created successfully!
            {circleId && ' Redirecting to circle...'}
          </Alert>
        )}

        {/* Form */}
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
        >
          {/* Circle Selection */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Where do you want to share?
            </Typography>
            <TextField
              select
              fullWidth
              value={circleId}
              onChange={(e) => setCircleId(e.target.value)}
              disabled={submitting || loadingCircles}
              SelectProps={{
                displayEmpty: true,
              }}
              sx={{
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }
              }}
            >
              <MenuItem value="">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PublicIcon color="action" fontSize="small" />
                  <span>Public (everyone can see)</span>
                </Box>
              </MenuItem>

              {loadingCircles ? (
                <MenuItem disabled>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Loading circles...
                </MenuItem>
              ) : circles.length === 0 ? (
                <MenuItem disabled>
                  <em>No circles joined yet</em>
                </MenuItem>
              ) : (
                circles.map((circle) => (
                  <MenuItem key={circle.id} value={circle.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      {circle.is_private ? (
                        <LockIcon color="action" fontSize="small" />
                      ) : (
                        <GroupIcon color="primary" fontSize="small" />
                      )}
                      <span>{circle.name}</span>
                      <Chip
                        label={`${circle.member_count || 0}`}
                        size="small"
                        sx={{ ml: 'auto', height: 20, fontSize: '0.7rem' }}
                      />
                    </Box>
                  </MenuItem>
                ))
              )}
            </TextField>

            {circleId && getSelectedCircle() && (
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  icon={getSelectedCircle()?.is_private ? <LockIcon /> : <GroupIcon />}
                  label={`Posting to: ${getSelectedCircle()?.name}`}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              </Box>
            )}
          </Box>

          {/* Prayer Content */}
          <TextField
            label="What's on your heart?"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            multiline
            rows={5}
            required
            disabled={submitting}
            placeholder="Share your prayer request..."
            helperText={`${content.length}/1000 characters`}
            inputProps={{
              maxLength: 1000,
              'aria-label': 'Prayer content',
              'aria-required': 'true',
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          />

          {/* Category */}
          <TextField
            select
            label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            disabled={submitting}
            inputProps={{
              'aria-label': 'Prayer category'
            }}
          >
            <MenuItem value="General">General</MenuItem>
            <MenuItem value="Health">Health</MenuItem>
            <MenuItem value="Family">Family</MenuItem>
            <MenuItem value="Work">Work</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </TextField>

          {/* Anonymous Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={isAnonymous}
                onChange={(e) => setIsAnonymous(e.target.checked)}
                disabled={submitting}
                inputProps={{
                  'aria-label': 'Post prayer anonymously'
                }}
              />
            }
            label={
              <Box>
                <Typography variant="body2">Post anonymously</Typography>
                <Typography variant="caption" color="text.secondary">
                  Your name won't be shown with this prayer
                </Typography>
              </Box>
            }
          />

          {/* Submit Button */}
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={submitting || !content.trim()}
            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            sx={{
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
            }}
          >
            {submitting ? 'Posting...' : 'Post Prayer'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}

export default CreatePrayer;
